import type { HelpLinkProps } from "../../../../../../components/Questionnaire/Section/Question/HelpLink";
import type { Display } from "../../../../../../components/Questionnaire/Section/Question/QuestionFactory";
import type {
  AddressLookupGroup,
  QuestionProps,
} from "../../../../../../components/Questionnaire/Section/Question/types";
import { snakeCase } from "../../../../../../utils";
import type { Answers } from "../../../../types";

type CreateShadowQuestionParams = {
  title: string;
  subText: string;
  visibleQuestions?: QuestionProps[];
  answers: Answers;
  required: boolean;
  lastItemDisables?: boolean;
  helpInfo?: HelpLinkProps;
};

/**
 * Creates a "shadow" question structure for multiselect groups.
 * This shadow question consolidates visible questions into a single multiselect question.
 */
export const createShadowQuestion = ({
  title,
  subText,
  visibleQuestions = [],
  answers,
  required,
  lastItemDisables = false,
  helpInfo,
}: CreateShadowQuestionParams): {
  question: QuestionProps;
  answer: string[];
} => {
  const question: QuestionProps = {
    id: snakeCase(`shadow::${title}`),
    name: `Shadow::${title}`,
    primaryText: title,
    secondaryText: subText,
    visible: visibleQuestions.some(q => q.visible),
    includeAnswer: false,
    required,
    options: visibleQuestions.map(q => ({
      label: q.primaryText,
      value: q.name,
      shouldUpdate: q.shouldUpdate,
    })),
    display: { type: "multiselect", config: { lastItemDisables } } as Display,
  };

  if (helpInfo) {
    question.helpInfo = helpInfo;
  }

  return {
    question,
    answer: visibleQuestions
      .filter(q => answers?.[q.name] === "yes")
      .map(q => q.name),
  };
};

/**
 * Creates an address lookup controller question for address groups.
 * The display type acts as a pseudo primary question controlling address behaviour.
 * Visibility is derived from child questions - if any child question is visible,
 * the address lookup group is visible.
 */
export const createAddressLookupQuestion = ({
  title,
  countryCode,
  subText,
  manualEntryDisabled,
  apiKey,
  groupQuestions,
}: AddressLookupGroup): QuestionProps => {
  return {
    id: snakeCase(`shadow::${title}`),
    name: `Shadow::${title}`,
    primaryText: title,
    secondaryText: subText,
    visible: groupQuestions?.some(q => q.visible) ?? false,
    shouldUpdate: groupQuestions?.some(q => q.shouldUpdate) ?? false,
    includeAnswer: false,
    // The display type acts as a pseudo primary question controlling address behaviour
    display: {
      type: "address_lookup_group",
      config: {
        countryCode,
        manualEntryDisabled,
        apiKey,
      },
    } as Display,
  };
};
